import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { Table, TableRow, TableCell, TableHeader, TableView } from '@tiptap/extension-table';
import Paragraph from '@tiptap/extension-paragraph';
import ListItem from '@tiptap/extension-list-item';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatColorFill,
  FormatColorText,
  Undo,
  Redo,
  TableChart,
  DeleteOutline,
  CallMerge,
  CallSplit,
  ArrowUpward,
  ArrowDownward,
  ArrowBack,
  ArrowForward,
  PlaylistRemove,
  ViewColumn,
  Settings,
} from '@mui/icons-material';
import TablePropertiesDialog from './TablePropertiesDialog';
import LockTableColumnWidths from '../../extensions/LockTableColumnWidths';
import '../../utils/font.css';

const AVAILABLE_VARIABLES = [
  { key: 'nome', label: 'Nome do Cliente' },
  { key: 'Budget', label: 'Tabela de Orçamento' },
];

const FONT_FAMILIES = [
  { value: 'Montserrat, Arial, Helvetica, sans-serif', label: 'Montserrat' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Courier New, monospace', label: 'Courier New' },
];

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px'];

const TEXT_COLORS = [
  { value: '#172b4d', label: 'Texto padrão' },
  { value: '#000000', label: 'Preto' },
  { value: '#FFFFFF', label: 'Branco' },
  { value: '#082B49', label: 'Navy marca' },
  { value: '#0D3D5C', label: 'Azul Costão' },
  { value: '#00365d', label: 'Azul template' },
  { value: '#002060', label: 'Azul assinatura' },
  { value: '#1F3864', label: 'Azul escuro' },
  { value: '#FBAF17', label: 'Dourado marca' },
  { value: '#C62828', label: 'Vermelho' },
  { value: '#2F9E44', label: 'Verde' },
  { value: '#1565C0', label: 'Azul link' },
  { value: '#6b778c', label: 'Cinza' },
];

const HIGHLIGHT_COLORS = [
  { value: '#fff59d', label: 'Amarelo' },
  { value: '#FFE082', label: 'Dourado claro' },
  { value: '#FBAF17', label: 'Dourado marca' },
  { value: '#C8E6C9', label: 'Verde claro' },
  { value: '#BBDEFB', label: 'Azul claro' },
  { value: '#E3F2FD', label: 'Azul gelo' },
  { value: '#F8BBD0', label: 'Rosa claro' },
  { value: '#FFCCBC', label: 'Laranja claro' },
  { value: '#E4EBF2', label: 'Cinza azul' },
  { value: '#F3F6F9', label: 'Cinza suave' },
];

const wrapTemplateVariables = (html) => {
  if (!html) return '';
  return html.replace(
    /<span[^>]*class="[^"]*mceNonEditable[^"]*"[^>]*>\{\{\{[^}]+\}\}\}<\/span>|\{\{\{([^}]+)\}\}\}/g,
    (match, key) => {
      if (!key) return match;
      return `<span class="mceNonEditable" data-variable="${key}">{{{${key}}}}</span>`;
    }
  );
};

const EmailImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width') || element.style.width || null,
        renderHTML: (attributes) => (attributes.width ? { width: attributes.width } : {}),
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute('height') || element.style.height || null,
        renderHTML: (attributes) => (attributes.height ? { height: attributes.height } : {}),
      },
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => (attributes.style ? { style: attributes.style } : {}),
      },
    };
  },
}).configure({
  inline: true,
  allowBase64: true,
});

const cellStyleAttributes = {
  style: {
    default: null,
    parseHTML: (element) => element.getAttribute('style'),
    renderHTML: (attributes) => (attributes.style ? { style: attributes.style } : {}),
  },
  align: {
    default: null,
    parseHTML: (element) => element.getAttribute('align'),
    renderHTML: (attributes) => (attributes.align ? { align: attributes.align } : {}),
  },
  valign: {
    default: null,
    parseHTML: (element) => element.getAttribute('valign'),
    renderHTML: (attributes) => (attributes.valign ? { valign: attributes.valign } : {}),
  },
};

const applyTableVisualStyles = (table, styleAttr) => {
  if (!styleAttr) return;

  styleAttr.split(';').forEach((part) => {
    const [rawKey, ...rest] = part.split(':');
    if (!rawKey || rest.length === 0) return;
    const key = rawKey.trim().toLowerCase();
    const value = rest.join(':').trim();
    if (!key || !value || key === 'width' || key === 'min-width' || key === 'max-width') {
      return;
    }
    table.style.setProperty(key, value);
  });
};

class ResizableEmailTableView extends TableView {
  constructor(node, cellMinWidth, view, HTMLAttributes) {
    super(node, cellMinWidth, view, HTMLAttributes);
    applyTableVisualStyles(this.table, node.attrs?.style);
  }

  update(node) {
    const result = super.update(node);
    if (result) {
      applyTableVisualStyles(this.table, node.attrs?.style);
    }
    return result;
  }
}

const EmailTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => (attributes.style ? { style: attributes.style } : {}),
      },
      cellPadding: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute('data-cell-padding') || element.getAttribute('cellpadding'),
        renderHTML: (attributes) =>
          attributes.cellPadding ? { 'data-cell-padding': attributes.cellPadding } : {},
      },
      cellSpacing: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute('data-cell-spacing') || element.getAttribute('cellspacing'),
        renderHTML: (attributes) =>
          attributes.cellSpacing ? { 'data-cell-spacing': attributes.cellSpacing } : {},
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => (attributes.width ? { width: attributes.width } : {}),
      },
      align: {
        default: null,
        parseHTML: (element) => element.getAttribute('align'),
        renderHTML: (attributes) => (attributes.align ? { align: attributes.align } : {}),
      },
      border: {
        default: null,
        parseHTML: (element) => element.getAttribute('border'),
        renderHTML: (attributes) => (attributes.border ? { border: attributes.border } : {}),
      },
    };
  },
}).configure({
  resizable: true,
  lastColumnResizable: true,
  handleWidth: 10,
  cellMinWidth: 48,
  View: ResizableEmailTableView,
});

const EmailTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...cellStyleAttributes,
    };
  },
});

const EmailTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...cellStyleAttributes,
    };
  },
});

const GenericDiv = Node.create({
  name: 'div',
  group: 'block',
  content: 'block*',
  parseHTML() {
    return [{ tag: 'div' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', HTMLAttributes, 0];
  },
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute('style'),
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
      class: {
        default: null,
        parseHTML: (el) => el.getAttribute('class'),
        renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
      },
      align: {
        default: null,
        parseHTML: (el) => el.getAttribute('align'),
        renderHTML: (attrs) => (attrs.align ? { align: attrs.align } : {}),
      },
    };
  },
});

const PreserveStylesAndClasses = Extension.create({
  name: 'preserveStylesAndClasses',
  addGlobalAttributes() {
    return [
      {
        types: [
          'paragraph',
          'heading',
          'textStyle',
          'table',
          'tableRow',
          'tableCell',
          'tableHeader',
          'image',
          'listItem',
          'bulletList',
          'orderedList',
        ],
        attributes: {
          style: {
            default: null,
            parseHTML: (el) => el.getAttribute('style'),
            renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
          },
          class: {
            default: null,
            parseHTML: (el) => el.getAttribute('class'),
            renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
          },
        },
      },
    ];
  },
});

const TemplateVariableNode = Node.create({
  name: 'templateVariable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: (element) => {
          const dataKey = element.getAttribute('data-variable');
          if (dataKey) return dataKey;
          const text = element.textContent || '';
          const match = text.match(/\{\{\{([^}]+)\}\}\}|\{([^}]+)\}/);
          return match ? (match[1] || match[2]).trim() : text.trim();
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span.mceNonEditable' }];
  },

  renderHTML({ node }) {
    return [
      'span',
      mergeAttributes({ class: 'mceNonEditable', 'data-variable': node.attrs.key }),
      `{{{${node.attrs.key}}}}`,
    ];
  },
});

const EmailParagraph = Paragraph.extend({
  renderHTML({ HTMLAttributes }) {
    let style = HTMLAttributes.style || '';
    if (!style.includes('margin')) {
      style = `margin: 0; padding: 0; ${style}`;
    }
    return ['p', { ...HTMLAttributes, style: style.trim() }, 0];
  },
});

const EmailListItem = ListItem.extend({
  renderHTML({ HTMLAttributes }) {
    let style = HTMLAttributes.style || '';
    if (!style.includes('margin')) {
      style = `margin: 0; padding: 0; ${style}`;
    }
    return ['li', { ...HTMLAttributes, style: style.trim() }, 0];
  },
});

const TableControls = ({ editor, onOpenProperties, compact = false }) => {
  const buttonProps = compact
    ? {
        onMouseDown: (event) => event.preventDefault(),
      }
    : {};

  return (
    <>
      <Tooltip title="Propriedades da tabela">
        <IconButton size="small" {...buttonProps} onClick={onOpenProperties}>
          <Settings fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Inserir linha acima">
        <IconButton
          size="small"
          {...buttonProps}
          onClick={() => editor.chain().focus().addRowBefore().run()}
        >
          <ArrowUpward fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Inserir linha abaixo">
        <IconButton
          size="small"
          {...buttonProps}
          onClick={() => editor.chain().focus().addRowAfter().run()}
        >
          <ArrowDownward fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir linha">
        <IconButton
          size="small"
          color="error"
          {...buttonProps}
          onClick={() => editor.chain().focus().deleteRow().run()}
        >
          <PlaylistRemove fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <Tooltip title="Inserir coluna à esquerda">
        <IconButton
          size="small"
          {...buttonProps}
          onClick={() => editor.chain().focus().addColumnBefore().run()}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Inserir coluna à direita">
        <IconButton
          size="small"
          {...buttonProps}
          onClick={() => editor.chain().focus().addColumnAfter().run()}
        >
          <ArrowForward fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir coluna">
        <IconButton
          size="small"
          color="error"
          {...buttonProps}
          onClick={() => editor.chain().focus().deleteColumn().run()}
        >
          <ViewColumn fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <Tooltip title="Mesclar células">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().mergeCells()}
            {...buttonProps}
            onClick={() => editor.chain().focus().mergeCells().run()}
          >
            <CallMerge fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Dividir célula">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().splitCell()}
            {...buttonProps}
            onClick={() => editor.chain().focus().splitCell().run()}
          >
            <CallSplit fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Excluir tabela">
        <IconButton
          size="small"
          color="error"
          {...buttonProps}
          onClick={() => editor.chain().focus().deleteTable().run()}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );
};

const editorExtensions = [
  StarterKit.configure({
    paragraph: false,
    listItem: false,
    link: {
      openOnClick: false,
    },
  }),
  TextStyleKit,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  EmailImage,
  LockTableColumnWidths,
  EmailTable,
  TableRow,
  EmailTableHeader,
  EmailTableCell,
  TemplateVariableNode,
  GenericDiv,
  PreserveStylesAndClasses,
  EmailListItem,
  EmailParagraph,
];

export default function EditorTiptap({ value, onChange }) {
  const [varsAnchor, setVarsAnchor] = useState(null);
  const [textColorAnchor, setTextColorAnchor] = useState(null);
  const [highlightAnchor, setHighlightAnchor] = useState(null);
  const [tablePropertiesOpen, setTablePropertiesOpen] = useState(false);
  const [, setSelectionTick] = useState(0);
  const isInternalUpdate = useRef(false);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: editorExtensions,
    content: wrapTemplateVariables(value),
    editorProps: {
      attributes: {
        class: 'tiptap-email-editor',
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      isInternalUpdate.current = true;
      onChange?.(currentEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor || isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const nextContent = wrapTemplateVariables(value);
    if (nextContent !== editor.getHTML()) {
      editor.commands.setContent(nextContent, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    const refreshToolbar = () => setSelectionTick((tick) => tick + 1);
    editor.on('selectionUpdate', refreshToolbar);
    return () => {
      editor.off('selectionUpdate', refreshToolbar);
    };
  }, [editor]);

  const insertVariable = useCallback(
    (key) => {
      editor
        ?.chain()
        .focus()
        .insertContent({
          type: 'templateVariable',
          attrs: { key },
        })
        .run();
      setVarsAnchor(null);
    },
    [editor]
  );

  const handleInsertSignature = () => {
    editor
      ?.chain()
      .focus()
      .insertContent(
        '<p style="margin: 0; padding: 0; font-size: 14px; color: #082B49;"><strong>Atenciosamente,</strong></p><p style="margin: 0; padding: 0; font-size: 14px; color: #082B49;">Equipe Costão do Santinho</p>'
      )
      .run();
  };

  if (!editor) return null;

  const currentFontFamily =
    editor.getAttributes('textStyle').fontFamily || FONT_FAMILIES[0].value;
  const currentFontSize = editor.getAttributes('textStyle').fontSize || '14px';

  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          p: 1,
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #ccc',
        }}
      >
        <Tooltip title="Desfazer">
          <IconButton size="small" onClick={() => editor.chain().focus().undo().run()}>
            <Undo fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Refazer">
          <IconButton size="small" onClick={() => editor.chain().focus().redo().run()}>
            <Redo fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Select
          size="small"
          value={FONT_FAMILIES.some((font) => font.value === currentFontFamily)
            ? currentFontFamily
            : FONT_FAMILIES[0].value}
          onChange={(event) => editor.chain().focus().setFontFamily(event.target.value).run()}
          sx={{ minWidth: 120, bgcolor: '#fff', height: 32 }}
        >
          {FONT_FAMILIES.map((font) => (
            <MenuItem key={font.value} value={font.value} sx={{ fontFamily: font.value }}>
              {font.label}
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          value={FONT_SIZES.includes(currentFontSize) ? currentFontSize : '14px'}
          onChange={(event) => editor.chain().focus().setFontSize(event.target.value).run()}
          sx={{ minWidth: 80, bgcolor: '#fff', height: 32 }}
        >
          {FONT_SIZES.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Negrito">
          <IconButton
            size="small"
            color={editor.isActive('bold') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBold fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Itálico">
          <IconButton
            size="small"
            color={editor.isActive('italic') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalic fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Sublinhado">
          <IconButton
            size="small"
            color={editor.isActive('underline') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Cor do texto">
          <IconButton
            size="small"
            onClick={(e) => setTextColorAnchor(e.currentTarget)}
            sx={{ color: editor.getAttributes('textStyle').color || '#172b4d' }}
          >
            <FormatColorText fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={textColorAnchor}
          open={Boolean(textColorAnchor)}
          onClose={() => setTextColorAnchor(null)}
        >
          <Box
            sx={{
              px: 1.5,
              pt: 1,
              pb: 0.5,
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 0.75,
              width: 200,
            }}
          >
            {TEXT_COLORS.map((color) => (
              <Tooltip key={color.value} title={color.label}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setColor(color.value).run();
                    setTextColorAnchor(null);
                  }}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    border:
                      color.value === '#FFFFFF' ? '1px solid #ccc' : '1px solid rgba(0,0,0,0.15)',
                    bgcolor: color.value,
                    cursor: 'pointer',
                    p: 0,
                  }}
                />
              </Tooltip>
            ))}
          </Box>
          <MenuItem
            onClick={() => {
              editor.chain().focus().unsetColor().run();
              setTextColorAnchor(null);
            }}
          >
            Remover cor
          </MenuItem>
        </Menu>

        <Tooltip title="Cor de fundo">
          <IconButton
            size="small"
            color={editor.getAttributes('textStyle').backgroundColor ? 'primary' : 'default'}
            onClick={(e) => setHighlightAnchor(e.currentTarget)}
          >
            <FormatColorFill fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={highlightAnchor}
          open={Boolean(highlightAnchor)}
          onClose={() => setHighlightAnchor(null)}
        >
          <Box
            sx={{
              px: 1.5,
              pt: 1,
              pb: 0.5,
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 0.75,
              width: 180,
            }}
          >
            {HIGHLIGHT_COLORS.map((color) => (
              <Tooltip key={color.value} title={color.label}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setBackgroundColor(color.value).run();
                    setHighlightAnchor(null);
                  }}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    border: '1px solid rgba(0,0,0,0.15)',
                    bgcolor: color.value,
                    cursor: 'pointer',
                    p: 0,
                  }}
                />
              </Tooltip>
            ))}
          </Box>
          <MenuItem
            onClick={() => {
              editor.chain().focus().unsetBackgroundColor().run();
              setHighlightAnchor(null);
            }}
          >
            Remover fundo
          </MenuItem>
        </Menu>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Alinhar à esquerda">
          <IconButton
            size="small"
            color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <FormatAlignLeft fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Centralizar">
          <IconButton
            size="small"
            color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <FormatAlignCenter fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alinhar à direita">
          <IconButton
            size="small"
            color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <FormatAlignRight fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Justificar">
          <IconButton
            size="small"
            color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          >
            <FormatAlignJustify fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Lista">
          <IconButton
            size="small"
            color={editor.isActive('bulletList') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulleted fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Lista numerada">
          <IconButton
            size="small"
            color={editor.isActive('orderedList') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Inserir tabela">
          <IconButton
            size="small"
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
          >
            <TableChart fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button size="small" variant="outlined" onClick={(e) => setVarsAnchor(e.currentTarget)}>
          Variáveis
        </Button>
        <Menu anchorEl={varsAnchor} open={Boolean(varsAnchor)} onClose={() => setVarsAnchor(null)}>
          {AVAILABLE_VARIABLES.map((variable) => (
            <MenuItem key={variable.key} onClick={() => insertVariable(variable.key)}>
              {variable.label}
            </MenuItem>
          ))}
        </Menu>

        <Button size="small" variant="outlined" onClick={handleInsertSignature}>
          Assinatura
        </Button>

        <Button
          size="small"
          variant="outlined"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          Limpar
        </Button>
      </Box>

      {editor.isActive('table') && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.75,
            bgcolor: '#E8EEF3',
            borderBottom: '1px solid #C8D3DE',
          }}
        >
          <TableControls
            editor={editor}
            onOpenProperties={() => setTablePropertiesOpen(true)}
          />
        </Box>
      )}

      <Box
        sx={{
          minHeight: 500,
          maxHeight: 700,
          overflow: 'auto',
          px: 2,
          py: 1,
        }}
      >
        <EditorContent editor={editor} />
        <BubbleMenu
          editor={editor}
          options={{
            placement: 'top',
            offset: 8,
          }}
          shouldShow={({ editor: currentEditor }) => currentEditor.isActive('table')}
        >
          <Box
            onMouseDown={(event) => event.preventDefault()}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.75,
              bgcolor: '#fff',
              border: '1px solid #C8D3DE',
              borderRadius: 1.5,
              maxWidth: 520,
              pointerEvents: 'auto',
            }}
          >
            <TableControls
              editor={editor}
              compact
              onOpenProperties={() => setTablePropertiesOpen(true)}
            />
          </Box>
        </BubbleMenu>
      </Box>

      <TablePropertiesDialog
        open={tablePropertiesOpen}
        editor={editor}
        onClose={() => setTablePropertiesOpen(false)}
      />
    </Box>
  );
}
